import React, { useState, useEffect, useMemo } from "react";
import CalendarPicker from "./Calender";
import LeaveRequestForm from "./LeaveRequestForm";
import LeaveList from "./LeaveList";
import { useApiClient } from "../../service/API";
import { useAuth } from "../../auth/AuthHandler";
import { toast } from 'react-toastify';
import { getApiErrorMessage } from "../../service/errorHandler";
import NotificationModal from "../TicketComponents/NotificationModal";
import "../../styling/Leave.css"

export default function LeaveRequestPage ({}) {
  const { expiration, authenticated, userEmail, userName, userRoles, handleLogout, handleLogin } = useAuth();
  const [leaveRequestInfo, setLeaveRequestInfo] = useState({
    email: null, startDate: null, endDate: null, duration: null,
    reason: null, category: null, backup: null, timePeriod: null
  });
  const [leaveAvailable, setLeaveAvailable] = useState({});
  const [leaveList, setLeaveList] = useState([]);
  const [dayLeaveList, setDayLeaveList] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userList, setUserList] = useState([]);
  const apiClient = useApiClient();
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [onLeaveList, setOnLeaveList] = useState([]);

  const fetchonLeaveList = async () => {
    try {
      const response = await apiClient.get('/users/onleave-list');
      if (response.status === 200) {
        setOnLeaveList(response.data);
      }
    } catch (error) {
        setErrorMessage(getApiErrorMessage(error, "Error fetching user list."));
        setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setErrorMessage('');
  };

  const getPublicHoliday = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/holiday/get");
      setPublicHolidays(response.data);
    } catch (ex) {
      console.error(ex);
    } finally {
      setLoading(false);
    }
  };

  const setInfo = (name, value) => {
    setLeaveRequestInfo(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const getLeaveAvailable = async () => {
    if (['BOUSER'].some(role => userRoles.includes(role))) {
      try {
        setLoading(true);
        const response = await apiClient.get("/users/leave-available");
        if (response.status === 200) {
          setLeaveAvailable(response.data);
        }
      } catch (ex) {
        console.error(ex);
      } finally {
        setLoading(false);
      }
    }
  };

  const getOwnLeaveList = async () => {
    if (['BOUSER'].some(role => userRoles.includes(role))) {
      try {
        setLoading(true);
        const response = await apiClient.get("/users/own-leave-request-list");
        if (response.status === 200) {
          setLeaveList(response.data.data);
          setDayLeaveList(response.data.days);
        }
      } catch (ex) {
        console.error(ex);
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchUserList = async () => {
    try {
      const userListGet = await apiClient.get(`/users/bouser-list-leave`);
      setUserList(userListGet.data);
    } catch (error) {
      // handleApiError(error, 'Error fetching user list. Please contact IT support.');
    }
  };

  // Helper: reset form after success. Preserve email for BOUSER auto-fill UX.
  const resetRequestInfo = () => {
    const preserveEmail = ['BOUSER'].some(role => userRoles.includes(role));
    setLeaveRequestInfo(prev => ({
      email: preserveEmail ? prev.email : null,
      startDate: null,
      endDate: null,
      duration: null,
      reason: null,
      category: null,
      backup: null,
      timePeriod: null
    }));
  };

  // Trigger the leave request after confirmation
  const handleRequestLeave = async (info) => {
    if (['BOUSER'].some(role => userRoles.includes(role))) {
      try {
        const response = await apiClient.post(
          "/users/request-leave",
          info,
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (response.status === 200) {
          toast(response.data);
          // refresh data
          await Promise.all([getLeaveAvailable(), getOwnLeaveList(), fetchonLeaveList()]);
          // reset form state
          resetRequestInfo();
          // show success
          setErrorMessage("Leave apply success!");
          setShowModal(true);
        }
      } catch (ex) {
        const errorMsg = getApiErrorMessage(ex, "Leave apply failed!");
        setErrorMessage(errorMsg);
        setShowModal(true);
      }
    } else {
      try {
        const response = await apiClient.post(
          "/users/update-user-to-leave",
          info,
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (response.status === 200) {
          toast(response.data);
          // refresh data
          await Promise.all([getLeaveAvailable(), getOwnLeaveList(),fetchonLeaveList()]);
          // reset form state
          resetRequestInfo();
          // show success
          setErrorMessage("Leave success!");
          setShowModal(true);
        }
      } catch (ex) {
        const errorMsg = getApiErrorMessage(ex, "Leave create failed!");
        setErrorMessage(errorMsg);
        setShowModal(true);
      }
    }
  };

  useEffect(() => {
    if (!['BOMANAGER', 'ADMIN'].some(role => userRoles.includes(role))) {
      setInfo("email", userEmail);
    }
    fetchUserList();
    getPublicHoliday();
    getLeaveAvailable();
    fetchonLeaveList();
    getOwnLeaveList();
  }, [showModal]); // refetch after modal closes/opens

  // Get holiday object (including its name) if date is a public holiday
  const getHolidayForDate = (year, month, day) => {
    if (!day) return null;
    const inputDate = `${year}-${(month + 1)
      .toString()
      .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

    // returns the entire holiday (date, holidayName, id)
    return publicHolidays.find(holiday => holiday.date === inputDate) || null;
  };

  // Keep a boolean helper if you still need it
  const isPublicHoliday = (year, month, day) => !!getHolidayForDate(year, month, day);


  const calculateDurationExcludingWeekendsAndHolidays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    let total = 0;
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      const year = d.getFullYear();
      const month = d.getMonth();
      const day = d.getDate();
      const isHoliday = isPublicHoliday(year, month, day);
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday) total++;
    }
    return total;
  };

  useEffect(() => {
    if (!leaveRequestInfo.category?.includes("HALF DAY")) {
      setInfo("duration", calculateDurationExcludingWeekendsAndHolidays(leaveRequestInfo.startDate, leaveRequestInfo.endDate));
    }
  }, [leaveRequestInfo.startDate, leaveRequestInfo.endDate]);

  const onCancelButton = async (leaveId) => {
    try {
      await apiClient.patch(`/users/cancel-leave/${leaveId}`);
      setErrorMessage("Leave cancel success!");
      setShowModal(true);
      // refreshing lists after cancel is optional; if you want immediate refresh:
      await getOwnLeaveList();
      await getLeaveAvailable();
      await fetchonLeaveList();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <div className="leave-request-page">
      {(['BOUSER'].some(role => userRoles.includes(role))) &&
        <div className="leave-status">
          <h6>Annual Leave available: {leaveAvailable.annual}</h6>
          <h6>Medical Leave available: {leaveAvailable.medical}</h6>
          <h6>Hospital Leave available: {leaveAvailable.hospital}</h6>
          <h6>Unpaid Leave applied: {leaveAvailable.unpaid}</h6>
        </div>}
      <div className="leave-calender">
        <CalendarPicker
          info={leaveRequestInfo}
          setInfo={setInfo}
          isPublicHoliday={isPublicHoliday}
          getHolidayForDate={getHolidayForDate}
          leaveList={dayLeaveList}
          navTitle="leave/create-leave"
          leaveAvailable={leaveAvailable}
          onLeaveList={onLeaveList}
        />

        <LeaveRequestForm
          leaveAvailable={leaveAvailable}
          info={leaveRequestInfo}
          setInfo={setInfo}
          handleRequestLeave={handleRequestLeave}
          userList={userList}
          errorMessage={errorMessage}
          showModal={showModal}
          handleCloseModal={handleCloseModal}
          leaveList={dayLeaveList}
        />
      </div>
      
      {['BOUSER'].some(role => userRoles.includes(role)) &&
        <div className="own-leave-list">
          <LeaveList
            leavelist={leaveList}
            loading={false}
            onCancelButton={onCancelButton}
            title="own"
            userList={userList}
          />
        </div>}
      <NotificationModal
        show={showModal}
        message={errorMessage}
        onClose={handleCloseModal}
      />
    </div>
  );
}
